import { Play } from 'lucide-react';
import { ContentRail } from './ContentRail';
import LiveBadge from '../LiveBadge';
import { getTeamFlag } from '../../utils/teamFlags';

export function LiveNowSection({ liveContent, liveScores, matchMinutes, navigate, followedTeams = [] }) {
  if (!liveContent.length) return null;

  const hasFollowedLive = followedTeams.length > 0 && liveContent.some(c => {
    const t = (c.teams || '').toLowerCase();
    return followedTeams.some(ft => t.includes(ft));
  });

  return (
    <ContentRail
      icon={<LiveBadge variant="default" />}
      title="LIVE NOW"
      subtitle={hasFollowedLive ? 'Your team is playing right now' : `${liveContent.length} match${liveContent.length > 1 ? 'es' : ''} happening now`}
      seeAllPath="/feed"
      navigate={navigate}
      testId="live-now-section"
    >
      {liveContent.map((content) => {
        const teamsStr = content.teams || content.title || '';
        const parts = teamsStr.split(/ vs /i);
        const home = parts[0]?.trim() || teamsStr;
        const away = parts[1]?.trim() || '';
        const homeFlag = getTeamFlag(home);
        const awayFlag = getTeamFlag(away);
        const scores = liveScores[content.id];
        const minute = scores?.match_minute || matchMinutes[content.id] || '';

        return (
          <div
            key={content.id}
            onClick={() => navigate(`/match/${content.id}`)}
            className="flex-shrink-0 w-[260px] sm:w-[280px] bg-[#141414] border border-white/[0.06] hover:border-red-500/30 rounded-xl p-4 cursor-pointer group snap-start transition-all duration-200 hover:bg-[#1a1a1a]"
            data-testid={`live-card-${content.id}`}
          >
            {/* Top: Competition + minute */}
            <div className="flex items-center justify-between mb-3">
              {content.competition_name && (
                <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-wider truncate mr-2">{content.competition_name}</span>
              )}
              {minute && (
                <div className="flex items-center gap-1 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-[10px] font-semibold">{minute}</span>
                </div>
              )}
            </div>
            {/* Teams + scores */}
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                {homeFlag ? <span className="text-lg leading-none">{homeFlag}</span> : (
                  <span className="w-6 h-6 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-[8px] font-bold text-gray-300 shrink-0">{home.slice(0,3).toUpperCase()}</span>
                )}
                <span className="flex-1 text-white font-semibold text-sm truncate">{home}</span>
                {scores && <span className={`font-bold text-base tabular-nums ${scores.home_score > scores.away_score ? 'text-emerald-400' : 'text-white'}`}>{scores.home_score}</span>}
              </div>
              {away && (
                <div className="flex items-center gap-2.5">
                  {awayFlag ? <span className="text-lg leading-none">{awayFlag}</span> : (
                    <span className="w-6 h-6 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-[8px] font-bold text-gray-300 shrink-0">{away.slice(0,3).toUpperCase()}</span>
                  )}
                  <span className="flex-1 text-white font-semibold text-sm truncate">{away}</span>
                  {scores && <span className={`font-bold text-base tabular-nums ${scores.away_score > scores.home_score ? 'text-emerald-400' : 'text-white'}`}>{scores.away_score}</span>}
                </div>
              )}
            </div>
            {/* Followed team badge */}
            {followedTeams.length > 0 && (() => {
              const t = (content.teams || '').toLowerCase();
              const matchedTeam = followedTeams.find(ft => t.includes(ft));
              if (!matchedTeam) return null;
              return (
                <div className="mt-2 mb-1 flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full w-fit" data-testid={`your-team-badge-${content.id}`}>
                  <span className="text-emerald-400 text-[8px] font-bold">Your team is playing</span>
                </div>
              );
            })()}
            {/* Buttons */}
            <div className="flex gap-2 mt-2">
              <button className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold rounded-lg transition-colors uppercase tracking-wide">
                <Play className="w-3 h-3" fill="currentColor" />
                Watch
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/matchday/${content.id}`); }}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-white/[0.06] hover:bg-white/10 text-white/70 text-[10px] font-bold rounded-lg transition-colors uppercase tracking-wide border border-white/[0.06]"
                data-testid={`matchday-btn-${content.id}`}
              >
                Match Day
              </button>
            </div>
          </div>
        );
      })}
    </ContentRail>
  );
}
